import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AxiosError } from "axios";
import { Button, message, Space, Tag, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { SortOrder } from "antd/es/table/interface";
import Highlighter from "react-highlight-words";
import {
    CheckOutlined,
    CopyOutlined,
    MobileOutlined,
} from "@ant-design/icons";
import { FaWhatsapp } from "react-icons/fa";

import { IAccessCode } from "./interfaces";
import { formatCpf, formatPhone } from "../../utils/Masks";
import { formatDateStringToDateAndTime } from "../../utils/Conversores";
import { removeAcento } from "../../utils/RemoveAcentos";
import { ThemeContext } from "../../contexts/ThemeContext";
import useAuth from "../../hooks/useAuth";
import AccessCodeService from "../../services/http/services/AccessCodeService";
import { EmitError } from "../../utils/EmitError";
import TableActionButton from "../../components/TableActionButton";
import { IActiveFilterItem } from "../../components/ActiveFiltersBar";
import useTableColumnFilter from "../../hooks/useTableColumnFilter";

export interface ITableData extends IAccessCode {
    key: number;
    app_user_name: string;
    app_user_phone: string;
    app_user_cpf: string;
    counties_names: string;
}

export interface IAccessCodeFilters {
    search: string | null;
    phone: string | null;
    code: string | null;
    type: "WHATSAPP" | "SMS" | null;
    county: string | null;
}

export type SortField = "app_user_name" | "app_user_phone" | "code" | "type" | "created_at" | null;

export default function useAccessCode() {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === "dark";
    const { getColumnSearchProps, getColumnRadioProps } = useTableColumnFilter();

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [rawData, setRawData] = useState<Array<ITableData>>([]);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 25 });
    const [copiedCodeId, setCopiedCodeId] = useState<number | null>(null);

    const [filters, setFilters] = useState<IAccessCodeFilters>({
        search: null,
        phone: null,
        code: null,
        type: null,
        county: null,
    });

    const [sorter, setSorter] = useState<{ field: SortField; order: "asc" | "desc" | null }>({
        field: "created_at",
        order: "desc",
    });

    const [searchUserQuery, setSearchUserQuery] = useState("");
    const [searchPhoneQuery, setSearchPhoneQuery] = useState("");
    const [searchCodeQuery, setSearchCodeQuery] = useState("");
    const [searchCountyQuery, setSearchCountyQuery] = useState("");

    const navigate = useNavigate();
    const { activeCity } = useAuth();
    const isFirstLoadRef = useRef(true);

    const handleClickViewUser = useCallback(
        (data: ITableData) => {
            if (data.app_user?.id) {
                navigate(`/usuarios/aplicativo/visualizar/${data.app_user.id}`);
            } else {
                message.info("Identificador do cidadão não disponível.");
            }
        },
        [navigate]
    );

    const handleCopyCode = useCallback(async (record: ITableData) => {
        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(record.code);
            }
            setCopiedCodeId(record.id);
            message.success(`Código ${record.code} copiado com sucesso!`);
            setTimeout(() => {
                setCopiedCodeId((current) => (current === record.id ? null : current));
            }, 2000);
        } catch {
            message.error("Erro ao copiar código para a área de transferência.");
        }
    }, []);

    const handleFilterChange = useCallback((key: keyof IAccessCodeFilters, value: any) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, []);

    const handleClearAllFilters = useCallback(() => {
        setFilters({
            search: null,
            phone: null,
            code: null,
            type: null,
            county: null,
        });
        setSearchUserQuery("");
        setSearchPhoneQuery("");
        setSearchCodeQuery("");
        setSearchCountyQuery("");
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, []);

    const stats = useMemo(() => {
        const total = rawData.length;
        const whatsapp = rawData.filter((r) => r.type === "WHATSAPP").length;
        const sms = rawData.filter((r) => r.type === "SMS").length;

        const uniqueUserIds = new Set(
            rawData
                .map((r) => r.app_user?.id || r.app_user?.cpf || r.app_user?.phone)
                .filter(Boolean)
        );
        const uniqueUsers = uniqueUserIds.size;

        return { total, whatsapp, sms, uniqueUsers };
    }, [rawData]);

    const filteredData = useMemo(() => {
        let result = rawData;

        if (filters.search) {
            const query = removeAcento(filters.search.trim().toLowerCase());
            result = result.filter((item) => {
                const name = item.app_user_name ? removeAcento(item.app_user_name.toLowerCase()) : "";
                const cpfDigits = item.app_user_cpf ? item.app_user_cpf.replace(/\D/g, "") : "";
                const cpfRaw = item.app_user_cpf || "";
                return name.includes(query) || cpfDigits.includes(query) || cpfRaw.includes(query);
            });
        }

        if (filters.phone) {
            const queryDigits = filters.phone.replace(/\D/g, "");
            result = result.filter((item) => {
                const phoneDigits = item.app_user_phone ? item.app_user_phone.replace(/\D/g, "") : "";
                return phoneDigits.includes(queryDigits);
            });
        }

        if (filters.code) {
            const query = filters.code.trim().toLowerCase();
            result = result.filter((item) => (item.code || "").toLowerCase().includes(query));
        }

        if (filters.type) {
            result = result.filter((item) => item.type === filters.type);
        }

        if (filters.county) {
            const query = removeAcento(filters.county.trim().toLowerCase());
            result = result.filter((item) => {
                const countiesStr = removeAcento((item.counties_names || "").toLowerCase());
                return countiesStr.includes(query);
            });
        }

        return result;
    }, [rawData, filters]);

    const sortedData = useMemo(() => {
        if (!sorter.field || !sorter.order) return filteredData;

        return [...filteredData].sort((a, b) => {
            let valA: any = a[sorter.field as keyof ITableData];
            let valB: any = b[sorter.field as keyof ITableData];

            if (sorter.field === "created_at") {
                valA = valA ? new Date(valA).getTime() : 0;
                valB = valB ? new Date(valB).getTime() : 0;
            } else if (typeof valA === "string") {
                valA = removeAcento(valA.toLowerCase());
                valB = removeAcento((valB || "").toLowerCase());
            }

            if (valA < valB) return sorter.order === "asc" ? -1 : 1;
            if (valA > valB) return sorter.order === "asc" ? 1 : -1;
            return 0;
        });
    }, [filteredData, sorter]);

    const paginatedData = useMemo(() => {
        const start = (pagination.current - 1) * pagination.pageSize;
        return sortedData.slice(start, start + pagination.pageSize);
    }, [sortedData, pagination]);

    const activeFilterItems: IActiveFilterItem[] = useMemo(() => {
        const items: IActiveFilterItem[] = [];

        if (filters.search) {
            items.push({
                key: "search",
                label: "Cidadão",
                value: `"${filters.search}"`,
                onRemove: () => {
                    handleFilterChange("search", null);
                    setSearchUserQuery("");
                },
            });
        }

        if (filters.phone) {
            items.push({
                key: "phone",
                label: "Telefone",
                value: formatPhone(filters.phone),
                onRemove: () => {
                    handleFilterChange("phone", null);
                    setSearchPhoneQuery("");
                },
            });
        }

        if (filters.code) {
            items.push({
                key: "code",
                label: "Código",
                value: `"${filters.code}"`,
                onRemove: () => {
                    handleFilterChange("code", null);
                    setSearchCodeQuery("");
                },
            });
        }

        if (filters.type) {
            items.push({
                key: "type",
                label: "Canal",
                value: filters.type === "WHATSAPP" ? "WhatsApp" : "SMS",
                onRemove: () => handleFilterChange("type", null),
            });
        }

        if (filters.county) {
            items.push({
                key: "county",
                label: "Município",
                value: `"${filters.county}"`,
                onRemove: () => {
                    handleFilterChange("county", null);
                    setSearchCountyQuery("");
                },
            });
        }

        return items;
    }, [filters, handleFilterChange]);

    const handleTableChange = useCallback((newPagination: any, tableFilters: any, newSorter: any) => {
        if (Array.isArray(newSorter)) {
            newSorter = newSorter[0];
        }

        if (newSorter && newSorter.field) {
            const order = newSorter.order === "ascend" ? "asc" : newSorter.order === "descend" ? "desc" : null;
            setSorter({
                field: order ? (newSorter.field as SortField) : null,
                order,
            });
        } else {
            setSorter({ field: null, order: null });
        }
    }, []);

    const handlePaginationChange = useCallback((page: number, pageSize: number) => {
        setPagination({ current: page, pageSize });
    }, []);

    const columns: ColumnsType<ITableData> = useMemo(() => {
        const getSortOrder = (field: SortField): SortOrder | undefined => {
            if (sorter.field !== field) return undefined;
            return sorter.order === "asc" ? "ascend" : sorter.order === "desc" ? "descend" : undefined;
        };

        return [
            {
                title: "Cidadão / Solicitante",
                dataIndex: "app_user_name",
                key: "app_user_name",
                sorter: true,
                sortOrder: getSortOrder("app_user_name"),
                ...getColumnSearchProps({
                    title: "Filtrar por Cidadão",
                    placeholder: "Nome ou CPF...",
                    value: searchUserQuery,
                    onChange: setSearchUserQuery,
                    onSearch: (val) => handleFilterChange("search", val),
                    onClear: () => handleFilterChange("search", null),
                    isActive: Boolean(filters.search),
                }),
                render: (_: any, record: ITableData) => (
                    <div>
                        <div style={{ fontWeight: 600, color: isDark ? "#f0f0f0" : "#1e293b", fontSize: 13.5 }}>
                            <Highlighter
                                highlightStyle={{ backgroundColor: "#ffc069", padding: "0 2px" }}
                                searchWords={filters.search ? [filters.search] : []}
                                autoEscape
                                textToHighlight={record.app_user_name || "--"}
                            />
                        </div>
                        <div style={{ fontSize: 12, color: isDark ? "#8c8c8c" : "#64748b", marginTop: 1 }}>
                            CPF: {formatCpf(record.app_user_cpf) || "--"}
                        </div>
                    </div>
                ),
            },
            {
                title: "Telefone",
                dataIndex: "app_user_phone",
                key: "app_user_phone",
                width: 150,
                sorter: true,
                sortOrder: getSortOrder("app_user_phone"),
                ...getColumnSearchProps({
                    title: "Filtrar por Telefone",
                    placeholder: "DDD ou número...",
                    value: searchPhoneQuery,
                    onChange: setSearchPhoneQuery,
                    onSearch: (val) => handleFilterChange("phone", val),
                    onClear: () => handleFilterChange("phone", null),
                    isActive: Boolean(filters.phone),
                    formatter: formatPhone,
                }),
                render: (_: any, record: ITableData) => (
                    <Tag
                        style={{
                            margin: 0,
                            fontSize: 12,
                            background: isDark ? "#222" : "#f1f5f9",
                            borderColor: isDark ? "#333" : "#cbd5e1",
                            color: isDark ? "#ddd" : "#334155",
                            fontFamily: "monospace",
                            borderRadius: 4,
                            padding: "2px 8px",
                        }}
                    >
                        {formatPhone(record.app_user_phone) || "--"}
                    </Tag>
                ),
            },
            {
                title: "Código 2FA",
                dataIndex: "code",
                key: "code",
                width: 140,
                sorter: true,
                sortOrder: getSortOrder("code"),
                ...getColumnSearchProps({
                    title: "Filtrar por Código",
                    placeholder: "Ex: 123456",
                    value: searchCodeQuery,
                    onChange: setSearchCodeQuery,
                    onSearch: (val) => handleFilterChange("code", val),
                    onClear: () => handleFilterChange("code", null),
                    isActive: Boolean(filters.code),
                }),
                render: (_: any, record: ITableData) => {
                    const isCopied = copiedCodeId === record.id;
                    return (
                        <Space direction="horizontal" size={6}>
                            <Tag
                                color="blue"
                                style={{
                                    margin: 0,
                                    fontFamily: "monospace",
                                    fontSize: 13.5,
                                    fontWeight: 700,
                                    letterSpacing: 1.5,
                                    padding: "2px 8px",
                                    borderRadius: 4,
                                }}
                            >
                                {record.code}
                            </Tag>
                            <Tooltip title={isCopied ? "Copiado!" : "Copiar código"}>
                                <Button
                                    type="text"
                                    size="small"
                                    icon={isCopied ? <CheckOutlined style={{ color: "#52c41a" }} /> : <CopyOutlined />}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleCopyCode(record);
                                    }}
                                    style={{
                                        color: isCopied ? "#52c41a" : isDark ? "#aaa" : "#64748b",
                                        padding: 2,
                                    }}
                                />
                            </Tooltip>
                        </Space>
                    );
                },
            },
            {
                title: "Canal de Envio",
                dataIndex: "type",
                key: "type",
                width: 140,
                sorter: true,
                sortOrder: getSortOrder("type"),
                ...getColumnRadioProps({
                    title: "Filtrar por Canal",
                    value: filters.type || "",
                    onChange: (val) => handleFilterChange("type", (val as "WHATSAPP" | "SMS") || null),
                    onClear: () => handleFilterChange("type", null),
                    isActive: Boolean(filters.type),
                    options: [
                        { label: "Todos os Canais", value: "" },
                        { label: "WhatsApp", value: "WHATSAPP" },
                        { label: "SMS", value: "SMS" },
                    ],
                }),
                render: (_: any, record: ITableData) => {
                    if (record.type === "WHATSAPP") {
                        return (
                            <Tag
                                color="success"
                                icon={<FaWhatsapp style={{ verticalAlign: "middle", marginRight: 4 }} />}
                                style={{ borderRadius: 4, fontWeight: 500, padding: "2px 8px" }}
                            >
                                WhatsApp
                            </Tag>
                        );
                    }
                    return (
                        <Tag
                            color="orange"
                            icon={<MobileOutlined style={{ verticalAlign: "middle" }} />}
                            style={{ borderRadius: 4, fontWeight: 500, padding: "2px 8px" }}
                        >
                            SMS
                        </Tag>
                    );
                },
            },
            {
                title: "Município(s)",
                dataIndex: "counties_names",
                key: "counties_names",
                ...getColumnSearchProps({
                    title: "Filtrar por Município",
                    placeholder: "Nome do município...",
                    value: searchCountyQuery,
                    onChange: setSearchCountyQuery,
                    onSearch: (val) => handleFilterChange("county", val),
                    onClear: () => handleFilterChange("county", null),
                    isActive: Boolean(filters.county),
                }),
                render: (_: any, record: ITableData) => {
                    const counties = record.app_user?.counties || [];
                    const count = counties.length;

                    if (count === 0) {
                        return <span style={{ color: isDark ? "#666" : "#94a3b8" }}>--</span>;
                    }

                    const firstTwo = counties.slice(0, 2);
                    const remaining = counties.slice(2);
                    const tooltipContent = remaining.map((c) => c.name).join(", ");

                    return (
                        <div style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
                            {firstTwo.map((c) => (
                                <Tag
                                    key={c.id}
                                    style={{
                                        margin: 0,
                                        fontSize: 11.5,
                                        borderRadius: 4,
                                        background: isDark ? "#222" : "#f1f5f9",
                                        borderColor: isDark ? "#333" : "#e2e8f0",
                                        color: isDark ? "#ccc" : "#475569",
                                    }}
                                >
                                    {c.name}
                                </Tag>
                            ))}
                            {count > 2 && (
                                <Tooltip title={`Outros municípios: ${tooltipContent}`}>
                                    <Tag
                                        color="purple"
                                        style={{
                                            margin: 0,
                                            fontSize: 11,
                                            borderRadius: 4,
                                            cursor: "pointer",
                                            fontWeight: 500,
                                        }}
                                    >
                                        +{count - 2} mais
                                    </Tag>
                                </Tooltip>
                            )}
                        </div>
                    );
                },
            },
            {
                title: "Data e Hora",
                dataIndex: "created_at",
                key: "created_at",
                width: 165,
                sorter: true,
                sortOrder: getSortOrder("created_at"),
                render: (val: string) => (
                    <span style={{ fontSize: 12.5, color: isDark ? "#d9d9d9" : "#475569", whiteSpace: "nowrap" }}>
                        {val ? formatDateStringToDateAndTime(val, true) : "--"}
                    </span>
                ),
            },
            {
                title: "Ações",
                key: "actions",
                width: 80,
                align: "center" as const,
                render: (_: any, record: ITableData) => (
                    <TableActionButton
                        action="view"
                        tooltip="Visualizar Cadastro do Cidadão"
                        onClick={() => handleClickViewUser(record)}
                    />
                ),
            },
        ];
    }, [
        isDark,
        sorter,
        copiedCodeId,
        filters.search,
        filters.phone,
        filters.code,
        filters.type,
        filters.county,
        searchUserQuery,
        searchPhoneQuery,
        searchCodeQuery,
        searchCountyQuery,
        getColumnSearchProps,
        getColumnRadioProps,
        handleFilterChange,
        handleCopyCode,
        handleClickViewUser,
    ]);

    const setTableDataFromRequest = useCallback((data: Array<IAccessCode>) => {
        const newData: ITableData[] = data.map((item) => {
            const counties = item.app_user?.counties || [];
            const countiesNames = counties.map((c) => c.name).join(", ");
            return {
                ...item,
                key: item.id,
                app_user_name: item.app_user?.name || "Cidadão",
                app_user_phone: item.app_user?.phone || "",
                app_user_cpf: item.app_user?.cpf || "",
                counties_names: countiesNames,
            };
        });
        setRawData(newData);
    }, []);

    const fetchCodes = useCallback(
        async (controller: AbortSignal, silent = false) => {
            try {
                if (!silent) {
                    setIsLoading(true);
                }
                const data = await AccessCodeService.listAccessCode(controller);
                setTableDataFromRequest(data);
            } catch (err) {
                if (err instanceof AxiosError && err.name === "CanceledError") {
                    return;
                }
                EmitError(err, true, "access_code_load_error");
            } finally {
                if (!silent) {
                    setIsLoading(false);
                }
            }
        },
        [setTableDataFromRequest]
    );

    const reloadData = useCallback(async () => {
        const controller = new AbortController();
        await fetchCodes(controller.signal, false);
    }, [fetchCodes]);

    useEffect(() => {
        if (!activeCity) return;

        const controller = new AbortController();

        if (isFirstLoadRef.current) {
            isFirstLoadRef.current = false;
            fetchCodes(controller.signal, false);
        } else {
            fetchCodes(controller.signal, true);
        }

        const interval = setInterval(() => {
            fetchCodes(controller.signal, true);
        }, 5000);

        return () => {
            clearInterval(interval);
            controller.abort();
        };
    }, [activeCity, fetchCodes]);

    return {
        isLoading,
        columns,
        tableData: paginatedData,
        totalCount: sortedData.length,
        stats,
        pagination: {
            ...pagination,
            total: sortedData.length,
        },
        activeFilterItems,
        handleTableChange,
        handlePaginationChange,
        handleClearAllFilters,
        handleClickViewUser,
        reloadData,
    };
}
